import { DEFAULT_LOCALE, LOCALE_META, type Locale } from "./i18n/config";

// Lista curada (no exhaustiva) de países soportados por TMDB para el
// selector de región de Viva Cine — cubre Latinoamérica completa, España,
// y los mercados de streaming más comunes fuera de la región.
export const COUNTRIES: { code: string; name: string }[] = [
  { code: "AR", name: "Argentina" },
  { code: "BO", name: "Bolivia" },
  { code: "BR", name: "Brasil" },
  { code: "CA", name: "Canadá" },
  { code: "CL", name: "Chile" },
  { code: "CO", name: "Colombia" },
  { code: "CR", name: "Costa Rica" },
  { code: "CU", name: "Cuba" },
  { code: "DE", name: "Alemania" },
  { code: "DO", name: "República Dominicana" },
  { code: "EC", name: "Ecuador" },
  { code: "ES", name: "España" },
  { code: "FR", name: "Francia" },
  { code: "GB", name: "Reino Unido" },
  { code: "GT", name: "Guatemala" },
  { code: "HN", name: "Honduras" },
  { code: "IT", name: "Italia" },
  { code: "MX", name: "México" },
  { code: "NI", name: "Nicaragua" },
  { code: "PA", name: "Panamá" },
  { code: "PE", name: "Perú" },
  { code: "PR", name: "Puerto Rico" },
  { code: "PT", name: "Portugal" },
  { code: "PY", name: "Paraguay" },
  { code: "SV", name: "El Salvador" },
  { code: "US", name: "Estados Unidos" },
  { code: "UY", name: "Uruguay" },
  { code: "VE", name: "Venezuela" },
];

// En español se usa la lista curada de arriba; en otros idiomas, el nombre
// que trae el propio navegador/Node (Intl.DisplayNames), sin mantener una
// lista traducida a mano por idioma.
export function countryName(code: string, lang: Locale = DEFAULT_LOCALE): string {
  if (lang !== DEFAULT_LOCALE) {
    try {
      const name = new Intl.DisplayNames([LOCALE_META[lang].intl], { type: "region" }).of(code);
      if (name) return name;
    } catch {
      // Intl.DisplayNames no disponible: cae a la lista en español.
    }
  }
  const curated = COUNTRIES.find((c) => c.code === code)?.name;
  if (curated) return curated;
  // Fuera de la lista curada (un evento en cualquier país): el nombre en
  // español que trae Intl, o el código si no hay.
  try {
    return new Intl.DisplayNames([LOCALE_META[DEFAULT_LOCALE].intl], { type: "region" }).of(code) ?? code;
  } catch {
    return code;
  }
}

// Bandera vía Unicode (regional indicator symbols) — no se usa en web:
// Windows (Segoe UI Emoji) no tiene glifos de bandera y muestra el código
// de país en texto plano en vez del ícono. Se deja para mobile (iOS/Android
// sí las renderizan) — en web usar flagUrl() en su lugar.
export function countryFlag(code: string): string {
  return code
    .toUpperCase()
    .replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt(0)));
}

// Bandera como imagen (flagcdn.com, gratis, sin key) — funciona igual en
// todos los sistemas operativos/navegadores, a diferencia del emoji.
export function flagUrl(code: string): string {
  return `https://flagcdn.com/24x18/${code.toLowerCase()}.png`;
}

export function normalizeForSearch(value: string): string {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");
}

// Código ISO desde un código o un nombre de país en español o inglés
// ("Perú", "peru", "United States", "PE"); undefined si no se reconoce. Lo
// usa la validación previa del Excel (mismo criterio que parseCountry en el
// backend).
let countryNameIndex: Map<string, string> | null = null;

function normalizeCountryName(text: string): string {
  return normalizeForSearch(text).replace(/[^a-z]+/g, " ").trim();
}

export function parseCountryName(input: string): string | undefined {
  const raw = input.trim();
  if (!raw) return undefined;
  if (/^[A-Za-z]{2}$/.test(raw)) return raw.toUpperCase();
  if (!countryNameIndex) {
    countryNameIndex = new Map();
    try {
      const names = ["es", "en"].map((l) => new Intl.DisplayNames([l], { type: "region" }));
      for (let a = 65; a <= 90; a++) {
        for (let b = 65; b <= 90; b++) {
          const code = String.fromCharCode(a, b);
          for (const dn of names) {
            const name = dn.of(code);
            if (name && name !== code) countryNameIndex.set(normalizeCountryName(name), code);
          }
        }
      }
    } catch {
      // Sin Intl.DisplayNames: solo la lista curada.
    }
    for (const c of COUNTRIES) countryNameIndex.set(normalizeCountryName(c.name), c.code);
    for (const alias of ["eeuu", "ee uu", "usa"]) countryNameIndex.set(alias, "US");
  }
  return countryNameIndex.get(normalizeCountryName(raw));
}
